import { getEnvConfig } from '../../config/config';
import { IListSeasons } from '../../graphql-types/listSeasons';
import { IDualMatches, IOverviewResults } from '../../graphql-types/matches';
import { WTNPayload } from '../../graphql-types/wtn';

enum TournamentSubCategoryEnum {
  UNKNOWN = 'UNKNOWN',
  TOURNAMENT = 'TOURNAMENT',
  LEAGUE = 'LEAGUE',
  DUAL_MATCH = 'DUAL_MATCH',
}

export const division = (division: any) => {
  if (division) {
    switch (division) {
      case 'DIV_I':
        return 'Division I';
      case 'DIV_II':
        return 'Division II';
      case 'DIV_III':
        return 'Division III';
    }
  }

  return division.replace(/_/g, ' ');
};

export const divisionShort = (division: any) => {
  if (division) {
    switch (division) {
      case 'DIV_I':
        return 'D1';
      case 'DIV_II':
        return 'D2';
      case 'DIV_III':
        return 'D3';
    }
  }

  return division.replace(/_/g, ' ');
};

export const formatTeamName = (name: string) =>
  name
    .replace(/&nbsp;/g, ' ')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

export const getOpponentsRosterIds = (data: any, id: any) => {
  const rosterIdsData: string[] = [];

  if (data?.matchUps?.items) {
    data.matchUps.items.forEach((matchup: any) => {

      const playerSide = matchup.sides.find((side: any) =>
        side.players.some((player: any) => player.person.personID === id)
      );

      if (playerSide) {
        const opponentSide = matchup.sides.find(
          (side: any) => side.sideNumber !== playerSide.sideNumber
        );

        const opponentSchoolId = opponentSide?.extensions
          ?.find((team: any) => team?.name === 'schoolId' || team?.name === 'teamId')
          ?.value?.toUpperCase();

        if (opponentSchoolId) {
          rosterIdsData.push(opponentSchoolId);
        }
      }

      const matchupSide = matchup.sides.find((side: any) => side);
      const schoolId = matchupSide?.extensions
        ?.find((team: any) => team?.name === 'schoolId' || team?.name === 'teamId')
        ?.value?.toUpperCase();

      if (schoolId) {
        rosterIdsData.push(schoolId);
      }

    });
  }

  return [...new Set(rosterIdsData)];
};

export const parseData = (data: any, teamsData: any, id?: string) => {
  const newData: any = {
    singles: [],
    doubles: []
  };

  if (!id || typeof id === 'undefined' || id === null) {
    return newData;
  }

  if (!data?.matchUps) {
    return newData;
  }

  let items: any = data.matchUps.items;

  if (items) {
    items.forEach((matchup: any) => {
      /* Find which side this player is on */
      let playerSide: any = matchup.sides.find((side: any) =>
        side.players.some((player: any) => player.person.personID === id)
      );

      /* If this player is find on a side, then it's safe to parse the rest of the match */
      let opponentSide: any = matchup.sides.find(
        (side: any) => side?.sideNumber !== playerSide?.sideNumber
      );

      let schoolId: string = '';
      if (opponentSide?.extensions) {
        schoolId = opponentSide.extensions.find((team: any) => team?.name === 'teamId' || team?.name === 'schoolId')?.value;
      }

      let team: any = {};
      if (teamsData && schoolId) {
        team = teamsData.find(
          (team: any) => team?.groupId.toLowerCase() === schoolId.toLowerCase()
        );
      }

      const homeTeamId = matchup.extensions
        .find(({ name }: { name: string }) => name === 'homeTeamId')
        ?.value.toLowerCase();

      const awayTeamId = matchup.extensions
        .find(({ name }: { name: string }) => name === 'awayTeamId')
        ?.value.toLowerCase();

      const matchUpExtension = matchup.extensions.find(
        (extension: { name?: string }) => extension.name === 'awayWin'
      );

      const isAbandoned = matchup.status === 'ABANDONED';
      const isPlayerSideWinning = playerSide?.sideNumber === matchup?.winningSide;
      const winLoss = !isAbandoned && isPlayerSideWinning;

      // Split the time off to avoid timezone based issues
      // UI only displays the date so doesn't need the time here
      const matchupDate = matchup.start.split("T")[0]

      let newMatch = {
        date: matchupDate,
        thisPlayerSide: playerSide?.sideNumber,
        didWin: playerSide?.sideNumber === matchup.winningSide ? true : false,
        thisPlayerWon: winLoss,
        thisPlayers: {
          thisPlayerId: id,
          players: playerSide?.players ? [...playerSide.players] : []
        },
        opponentPlayers:
          typeof opponentSide !== 'undefined'
            ? {
                opponent: [...opponentSide.players],
                schoolId
              }
            : { opponent: [] },
        team: {
          id: team?.groupId,
          name: team?.groupName,
          isHomeTeam:
            (homeTeamId && homeTeamId !== team?.groupId) ||
            (awayTeamId && awayTeamId === team?.groupId),
          isAwayTeam:
            (awayTeamId && awayTeamId !== team?.groupId) ||
            (homeTeamId && homeTeamId === team?.groupId)
        },
        winningSide: matchup.winningSide,
        scores: { ...matchup.score, status: matchup.status },
        roundsAndPositions: {
          isDualMatch: matchup?.tournament?.subCategory === TournamentSubCategoryEnum.DUAL_MATCH,
          roundName: matchup?.roundName,
          collectionPosition: matchup?.collectionPosition
        },
        status: matchup?.status
      };

      if (matchup.type === 'SINGLES') {
        newData.singles.push(newMatch);
      }

      if (matchup.type === 'DOUBLES') {
        newData.doubles.push(newMatch);
      }
    });
  }

  return newData;
};

export const prepareSeasonsData = (listOfSeasons: IListSeasons[]) => {
  const sortedSeasonsASC = listOfSeasons
    .map((season: IListSeasons) => season)
    .sort((a: IListSeasons, b: IListSeasons) => {
      return a.endDate < b.endDate ? 1 : -1;
    });

  return sortedSeasonsASC.map((season: IListSeasons) => ({
    value: season?.id,
    label: season?.name ?? 'No season available'
  }));
};

const excludeAbandonedWinLossStats = (match: IOverviewResults, isPlayerPage: boolean) => {
  if (isPlayerPage) {
    return match.status !== 'ABANDONED';
  }

  return true;
};

export const formatedMatches = (matches: any[]) =>
  matches?.map((dualMatch: IDualMatches) => ({
    ...dualMatch,
    didWin: dualMatch?.contextualInfo.didWin,
    status: dualMatch?.status
  }));

export const overviewResults = (matches: IOverviewResults[], isPlayerPage = false) => {
  const hasConferenceMatches = matches?.some((match: IOverviewResults) => match.isConferenceMatch);
  const totalMatches = matches.filter(
    match => match?.didWin !== null && excludeAbandonedWinLossStats(match, isPlayerPage)
  ).length;
  const totalConferenceMatches = matches.filter(
    match => match?.didWin !== null && match.isConferenceMatch
  ).length;

  const overallWins = matches?.filter(
    (match: IOverviewResults) => match.didWin && excludeAbandonedWinLossStats(match, isPlayerPage)
  ).length;
  const overalLoss = matches?.filter(
    (match: IOverviewResults) =>
      !match.didWin && match.didWin !== null && excludeAbandonedWinLossStats(match, isPlayerPage)
  ).length;

  const conferenceWins = matches?.filter(
    (match: IOverviewResults) => match.isConferenceMatch && match.didWin
  ).length;
  const conferenceLoss = matches?.filter(
    (match: IOverviewResults) => match.isConferenceMatch && !match.didWin && match.didWin !== null
  ).length;

  return {
    overall: {
      win: overallWins,
      loss: overalLoss,
      winPercentage:
        overallWins === 0 || totalMatches === 0
          ? 0
          : ((overallWins / totalMatches) * 100).toFixed(0)
    },
    conference: hasConferenceMatches
      ? {
          win: conferenceWins,
          loss: conferenceLoss,
          winPercentage:
            conferenceWins === 0 || totalConferenceMatches === 0
              ? 0
              : ((conferenceWins / totalConferenceMatches) * 100).toFixed(0)
        }
      : undefined
  };
};

export const getConfigColorForAdornmentMark = (wtnData: WTNPayload) => {
  const color = getEnvConfig().features.wtnConfidenceBands.reduce((result, band) => {
    if (wtnData?.confidence >= band.min) {
      return band.color;
    } else {
      return result;
    }
  }, '');

  return color;
};
